import React from "react";
import {
  ArrowRight,
  Users,
  Target,
  Award,
  Heart,
  Mail,
  Lightbulb,
  Globe,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white/5 backdrop-blur p-6 shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "", ...props }) => (
  <img src={src} alt={alt} className={className} {...props} />
);

export default function About() {
  const { t } = useTranslation();
  const values = [
    {
      icon: Heart,
      title: t("about.values.items.passion.title"),
      description: t("about.values.items.passion.description"),
    },
    {
      icon: Target,
      title: t("about.values.items.excellence.title"),
      description: t("about.values.items.excellence.description"),
    },
    {
      icon: Users,
      title: t("about.values.items.collaboration.title"),
      description: t("about.values.items.collaboration.description"),
    },
    {
      icon: Lightbulb,
      title: t("about.values.items.innovation.title"),
      description: t("about.values.items.innovation.description"),
    },
    {
      icon: Globe,
      title: t("about.values.items.globalImpact.title"),
      description: t("about.values.items.globalImpact.description"),
    },
    {
      icon: Award,
      title: t("about.values.items.recognition.title"),
      description: t("about.values.items.recognition.description"),
    },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <section className="py-16 sm:py-20 bg-blue-50">
        <div className="w-full max-w-[100rem] mx-auto px-4 sm:px-6">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-6xl md:text-7xl font-black text-foreground mb-4 sm:mb-6">
              {t("about.hero.title1")}{" "}
              <span className="text-primary"> {t("about.hero.highlight")}</span>{" "}
              {t("about.hero.title2")}
            </h1>
            <p className="font-paragraph text-lg sm:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
              {t("about.hero.description")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background mt-4 sm:mt-8">
        <div className="w-full max-w-[100rem] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center mb-16 sm:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">
                {t("about.mission.title1")}{" "}
                <span className="text-primary">
                  {" "}
                  {t("about.mission.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-lg text-foreground/80 mb-4 sm:mb-6 leading-relaxed">
                {t("about.mission.description")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <Image
                src="/images/cover.jpeg"
                alt="Mission"
                className="rounded-lg shadow-xl w-full max-w-full h-auto"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1 flex justify-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Image
                src="/images/cover2.jpeg"
                alt="Vision"
                className="rounded-lg shadow-xl w-full max-w-full h-auto"
              />
            </motion.div>

            <motion.div
              className="order-1 lg:order-2 flex flex-col justify-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">
                {t("about.vision.title1")}{" "}
                <span className="text-secondary">
                  {t("about.vision.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-lg text-foreground/80 leading-relaxed mb-4 sm:mb-6">
                {t("about.vision.description")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-foreground text-background overflow-x-hidden">
        <div className="w-full max-w-[100rem] mx-auto px-4 sm:px-6">
          <div className="overflow-x-hidden">
            <motion.div
              className="text-center mb-12 sm:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4 sm:mb-6 text-white">
                {t("about.values.title1")}{" "}
                <span className="text-electric-blue">
                  {" "}
                  {t("about.values.highlight")}
                </span>
              </h2>

              <p className="font-paragraph text-base sm:text-xl text-white/80 max-w-3xl mx-auto">
                {t("about.values.description")}
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="h-full"
              >
                <Card className="bg-white/90 text-black p-6 rounded-xl shadow-md hover:shadow-xl h-full flex flex-col">
                  <value.icon className="h-10 w-10 sm:h-12 sm:w-12 text-primary mb-4" />
                  <h3 className="font-heading text-lg sm:text-xl font-bold mb-3">
                    {value.title}
                  </h3>
                  <p className="font-paragraph text-gray-700 flex-grow text-sm sm:text-base">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-50">
        <div className="w-full max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-heading text-5xl font-bold text-foreground mb-6">
              {t("about.history.title1")}{" "}
              <span className="text-primary">
                {" "}
                {t("about.history.highlight")}
              </span>
            </h2>
            <p className="font-paragraph text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("about.history.description")}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {[
                {
                  year: "02/25",
                  title: t("about.history.milestones.foundation.title"),
                  description: t(
                    "about.history.milestones.foundation.description",
                  ),
                },
                {
                  year: "09/25",
                  title: t("about.history.milestones.integration.title"),
                  description: t(
                    "about.history.milestones.integration.description",
                  ),
                },
                {
                  year: "09/25",
                  title: t("about.history.milestones.partnership1.title"),
                  description: t(
                    "about.history.milestones.partnership1.description",
                  ),
                },
                {
                  year: "09/25",
                  title: t("about.history.milestones.firstPrograms.title"),
                  description: t(
                    "about.history.milestones.firstPrograms.description",
                  ),
                },
                {
                  year: "10/25",
                  title: t("about.history.milestones.activities.title"),
                  description: t(
                    "about.history.milestones.activities.description",
                  ),
                },
                {
                  year: "12/25",
                  title: t("about.history.milestones.partnership2.title"),
                  description: t(
                    "about.history.milestones.partnership2.description",
                  ),
                },
                {
                  year: "02/26",
                  title: t("about.history.milestones.website.title"),
                  description: t(
                    "about.history.milestones.website.description",
                  ),
                },
              ].map((milestone, index) => (
                <motion.div
                  key={index}
                  className="
              flex flex-col sm:flex-row 
              items-center sm:items-start 
              text-center sm:text-left 
              gap-6 sm:gap-8
            "
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-20 h-20 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-heading font-bold text-lg">
                    {milestone.year}
                  </div>

                  <div>
                    <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                      {milestone.title}
                    </h3>
                    <p className="font-paragraph text-lg text-foreground/80">
                      {milestone.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-gradient-to-r from-primary to-secondary text-primary-foreground">
        <div className="w-full max-w-[100rem] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl px-6 sm:px-8 py-12 sm:py-16 text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
               {t("about.cta.title1")}{" "}
              <span className="text-background"> {t("about.cta.highlight")}</span>
            </h2>

            <p className="font-paragraph text-base sm:text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto mb-10">
               {t("about.cta.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/members"
                className="px-6 sm:px-8 py-3 rounded-lg bg-background text-foreground font-semibold hover:bg-background/80 shadow-lg"
              >
                {t("about.cta.becomeMember")}
              </Link>

              <Link
                to="/contact"
                className="px-6 sm:px-8 py-3 rounded-lg border border-white/40 text-primary-foreground font-semibold hover:bg-white hover:text-primary shadow-lg"
              >
                {t("about.cta.contact")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
